import { useEffect, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';

export interface IdeasScanStatus {
  total: number;
  completed: number;
  failed: number;
  titles: string[];
  faviconUrls?: Array<string | null>;
}

type ReelPhase = 'title' | 'reading' | 'sliding';

export function IdeasScanProgress({ progress }: { progress: IdeasScanStatus | null }) {
  const titles = progress?.titles || [];
  const titleKey = JSON.stringify(titles);
  const [cursor, setCursor] = useState(0);
  const [phase, setPhase] = useState<ReelPhase>('title');
  const [reducedMotion, setReducedMotion] = useState(false);
  const [failedFavicons, setFailedFavicons] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let timer: ReturnType<typeof setTimeout> | undefined;
    const showTitle = () => {
      setPhase('title');
      timer = setTimeout(() => {
        setPhase('reading');
        if (titles.length < 2) return;
        timer = setTimeout(() => {
          setPhase('sliding');
          timer = setTimeout(() => {
            setCursor(index => index + 1);
            showTitle();
          }, 300);
        }, 3000);
      }, 1000);
    };
    const restart = () => {
      clearTimeout(timer);
      setCursor(0);
      setReducedMotion(!!motion?.matches);
      if (motion?.matches) setPhase('reading');
      else if (titles.length) showTitle();
    };
    restart();
    motion?.addEventListener('change', restart);
    return () => {
      clearTimeout(timer);
      motion?.removeEventListener('change', restart);
    };
  }, [titleKey, titles.length]);

  if (!titles.length) return (
    <div className="flex h-40 w-full items-center justify-center gap-2 text-sm text-muted-foreground" role="status">
      <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      Finding pages…
    </div>
  );

  const rolling = titles.length > 1 && !reducedMotion;
  const offsets = rolling ? [-2, -1, 0, 1, 2] : [0];
  return (
    <div className="w-full" aria-label="Finding post ideas progress">
      <span className="sr-only" role="status">Reading your website to find post ideas.</span>
      <div className="ideas-page-reel" aria-hidden="true">
        <div className={`ideas-page-reel-track${phase === 'sliding' && rolling ? ' is-sliding' : ''}`}
          style={{ transform: `translateY(${rolling ? (phase === 'sliding' ? '-7rem' : '-3.5rem') : '3.5rem'})` }}>
          {offsets.map(offset => {
            const index = cursor + offset;
            const pageIndex = ((index % titles.length) + titles.length) % titles.length;
            const title = titles[pageIndex];
            const favicon = progress?.faviconUrls?.[pageIndex];
            const focused = offset === (phase === 'sliding' && rolling ? 1 : 0);
            const reading = focused && phase === 'reading';
            return (
              <div key={index} className={`ideas-page-reel-row${focused ? ' is-focused' : ''}`}>
                {favicon && !failedFavicons.has(favicon)
                  ? <img src={favicon} alt="" width={16} height={16} referrerPolicy="no-referrer"
                      className="h-4 w-4 shrink-0 object-contain"
                      onError={() => setFailedFavicons(previous => new Set(previous).add(favicon))} />
                  : <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
                <span className="min-w-0 flex-1 truncate" title={title}>{title}</span>
                <span className={`ideas-page-reel-reading${reading ? ' is-visible' : ''}`}>
                  <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" />
                  Reading
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
